import React from "react";
import { motion } from "framer-motion";
import { Instagram, ArrowUpRight } from "lucide-react";
import { Reveal } from "./Reveal";

const INSTAGRAM_PROFILE = "https://www.instagram.com/svscollegeofnursingbokaro/";

// Featured reels — append more reel shortcodes here as they are shared
const INSTAGRAM_REELS = ["DZHteG4RYJu", "DbKhx5RRm44"];

export default function InstagramReels() {
  return (
    <section className="bg-navy py-24 lg:py-32 relative overflow-hidden" data-testid="instagram-section">
      <div className="grain-overlay" />
      <div className="relative max-w-7xl mx-auto px-5 sm:px-8">
        <div className="grid lg:grid-cols-[1fr_auto] gap-8 items-end mb-14">
          <Reveal>
            <span className="text-xs tracking-[0.3em] uppercase text-gold font-mono">Instagram</span>
            <h2 className="font-serif text-4xl sm:text-5xl text-sand mt-4 leading-tight">
              Life at SVS, <span className="italic text-gold-gradient">in reels</span>
            </h2>
            <p className="text-sand/60 mt-5 max-w-xl leading-relaxed">
              Campus moments, clinical training and student celebrations — follow along on Instagram.
            </p>
          </Reveal>
          <Reveal delay={0.05}>
            <a
              href={INSTAGRAM_PROFILE}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="instagram-follow-btn"
              className="inline-flex items-center gap-2 rounded-full bg-gold text-navy font-bold px-7 py-4 transition-transform duration-300 hover:scale-105"
            >
              <Instagram size={18} /> Follow @svscollegeofnursingbokaro
            </a>
          </Reveal>
        </div>

        <div className="grid sm:grid-cols-2 xl:grid-cols-4 gap-6">
          {INSTAGRAM_REELS.map((code, i) => (
            <Reveal key={code} delay={i * 0.08}>
              <motion.div
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                className="bg-white rounded-[2px] overflow-hidden ring-1 ring-gold/20 shadow-[0_20px_60px_rgba(0,0,0,0.35)]"
                data-testid={`instagram-reel-${i}`}
              >
                <iframe
                  src={`https://www.instagram.com/reel/${code}/embed`}
                  title={`SVS College of Nursing Instagram reel ${i + 1}`}
                  className="w-full h-[560px] border-0"
                  scrolling="no"
                  loading="lazy"
                  allowFullScreen
                  allow="autoplay; clipboard-write; encrypted-media; picture-in-picture; web-share"
                />
              </motion.div>
            </Reveal>
          ))}

          {/* Profile CTA card fills the grid until more reels are added */}
          {INSTAGRAM_REELS.length < 4 && (
            <Reveal delay={INSTAGRAM_REELS.length * 0.08}>
              <motion.a
                href={INSTAGRAM_PROFILE}
                target="_blank"
                rel="noopener noreferrer"
                whileHover={{ y: -6 }}
                transition={{ duration: 0.3 }}
                data-testid="instagram-profile-card"
                className="flex flex-col items-center justify-center text-center rounded-[2px] ring-1 ring-gold/30 bg-navy-dark h-[560px] p-8 group"
              >
                <div className="h-16 w-16 rounded-full bg-gold/15 ring-1 ring-gold/40 flex items-center justify-center mb-6">
                  <Instagram size={30} className="text-gold" />
                </div>
                <div className="font-serif text-2xl text-sand leading-snug">
                  More reels on our Instagram
                </div>
                <p className="text-sand/55 text-sm mt-3 leading-relaxed">
                  New campus moments every week — tap to watch them all.
                </p>
                <span className="mt-6 inline-flex items-center gap-2 text-gold font-semibold text-sm">
                  Open Instagram <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
                </span>
              </motion.a>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
}
